import { ARTICLE_CARD_HORIZONTAL_SUMMARY_DEFAULT_STYLE } from "@components/Elements/Card/Article/ArticleCardConstants";
import {
  HORIZONTAL_PACKAGE_CARD_STYLE,
  VERTICAL_PACKAGE_CARD_STYLE_FIRST,
  VERTICAL_PACKAGE_CARD_STYLE_REST,
} from "@components/HomePageStoryPackages/StoryPackageConstants";

export type ArticleCardVariant =
  | "small" // image, title, date
  | "medium" // image, title, date, section/content access
  | "2md" // image, title, date, section, content access
  | "large" // image, title, date, section/content access/summary
  | "xl" // image, title, date, section, content access, summary after title
  | "xxl" // image, title, date, section/content access, summary after title
  | "featured"; // hero image, title, date, section/summary

export type ArticleCardElementsOrientation =
  | "vertical" // horizontal package
  | "vertical-reversed" // mata-mata stories listing, carousel
  | "vertical-summary" // mata-mata landing
  | "horizontal" // vertical package, section listing,
  | "horizontal-summary"; // tags page, author page, search page

export type ArticleCardElementProps = {
  hasHeader?: boolean;
  hasIcon?: boolean;
  hasNewIcon?: boolean;
  hasImage?: boolean;
  hasParagraph?: boolean;
  hasSectionName?: boolean;
  hasTitle?: boolean;
  dateVariant?: "dash" | "or";
  hasTags?: boolean;
  hasDateSideBySide?: boolean;
  hasParaWithTitle?: boolean;
};

export type ArticleCardStyleProps = {
  cardWrapper?: string;
  contentWrapper?: string;
  headerWrapper?: string;
  hero?: string;
  description?: string;
  dateStyle?: string;
  titleStyle?: string;
  titleHoverStyle?: string;
  imageStyle?: string;
  tagWrapper?: string;
  sectionNameWrapper?: string;
  sectionNameItem?: string;
  iconStyle?: string;
  sectionNameStyle?: string;
  dateTitleWrapper?: string;
  titleParaWrapper?: string;
};

export const ARTICLE_CARD_DEFAULT_ELEMENTS: ArticleCardElementProps = {
  hasTitle: true,
  hasImage: true,
  hasParaWithTitle: false,
};

export const ARTICLE_CARD_ELEMENTS: Record<
  ArticleCardElementsOrientation,
  Record<
    ArticleCardVariant,
    {
      elements: ArticleCardElementProps;
      styles: ArticleCardStyleProps;
    }
  >
> = {
  vertical: {
    small: {
      elements: {
        hasNewIcon: true,
        hasSectionName: true,
      },
      styles: HORIZONTAL_PACKAGE_CARD_STYLE,
    },
    medium: {
      elements: {
        hasNewIcon: false,
        hasSectionName: true,
      },
      styles: HORIZONTAL_PACKAGE_CARD_STYLE,
    },
    "2md": {
      elements: {},
      styles: {},
    },
    large: {
      elements: {
        hasNewIcon: true,
        hasSectionName: false,
      },
      styles: HORIZONTAL_PACKAGE_CARD_STYLE,
    },
    xl: {
      elements: {},
      styles: {},
    },
    xxl: {
      elements: {},
      styles: {},
    },
    featured: {
      elements: {
        hasNewIcon: true,
        hasSectionName: true,
      },
      styles: {
        ...HORIZONTAL_PACKAGE_CARD_STYLE,
        cardWrapper:
          "flex flex-row gap-xs xl:gap-0 xl:flex-col xl:items-center justify-center xl:basis-1/4",
        headerWrapper: "basis-3/10 md:basis-2/5 lg:basis-3/10 block w-full",
        contentWrapper:
          "basis-7/10 md:basis-3/5 lg:basis-7/10 w-full mb-0 gap-xs text-xxs",
        dateTitleWrapper: "flex flex-col-reverse gap-sm",
        sectionNameWrapper: "flex justify-between",
      },
    },
  },
  "vertical-reversed": {
    small: {
      elements: ARTICLE_CARD_DEFAULT_ELEMENTS,
      styles: {
        cardWrapper: "flex flex-col gap-0",
        imageStyle: "w-full",
      },
    },
    medium: {
      elements: ARTICLE_CARD_DEFAULT_ELEMENTS,
      styles: {
        cardWrapper: "flex flex-col items-center justify-center w-full px-xs",
        headerWrapper: "block w-full",
        contentWrapper: "w-full",
        hero: "mb-xxs",
      },
    },
    "2md": {
      elements: {},
      styles: {},
    },
    large: {
      elements: {},
      styles: {},
    },
    xl: {
      elements: {},
      styles: {},
    },
    xxl: {
      elements: {},
      styles: {},
    },
    featured: {
      elements: {},
      styles: {},
    },
  },
  "vertical-summary": {
    small: {
      elements: {
        ...ARTICLE_CARD_DEFAULT_ELEMENTS,
        hasParagraph: true,
        dateVariant: "dash",
      },
      styles: {
        cardWrapper: "flex flex-col",
        contentWrapper:
          "flex flex-col-reverse md:flex-col mt-2 md:mt-0 md:mb-1 flex-grow",
        imageStyle: "w-full",
        hero: "w-full",
      },
    },
    medium: {
      elements: {},
      styles: {},
    },
    "2md": {
      elements: {},
      styles: {},
    },
    large: {
      elements: {},
      styles: {},
    },
    xl: {
      elements: {},
      styles: {},
    },
    xxl: {
      elements: {},
      styles: {},
    },
    featured: {
      elements: {},
      styles: {},
    },
  },
  horizontal: {
    small: {
      elements: {
        ...ARTICLE_CARD_DEFAULT_ELEMENTS,
        hasNewIcon: true,
        hasSectionName: false,
        hasParagraph: false,
      },
      styles: VERTICAL_PACKAGE_CARD_STYLE_FIRST,
    },
    medium: {
      elements: {
        ...ARTICLE_CARD_DEFAULT_ELEMENTS,
        hasNewIcon: false,
        hasSectionName: true,
        hasParagraph: false,
      },
      styles: {
        ...VERTICAL_PACKAGE_CARD_STYLE_REST,
        contentWrapper:
          "basis-7/10 md:basis-3/5 lg:basis-7/10 w-full mb-0 gap-xs",
      },
    },
    "2md": {
      elements: {
        ...ARTICLE_CARD_DEFAULT_ELEMENTS,
        hasNewIcon: true,
        hasSectionName: true,
        hasParagraph: false,
      },
      styles: {
        ...VERTICAL_PACKAGE_CARD_STYLE_REST,
        contentWrapper:
          "basis-7/10 md:basis-3/5 lg:basis-7/10 w-full mb-0 gap-xs",
      },
    },
    large: {
      elements: {
        ...ARTICLE_CARD_DEFAULT_ELEMENTS,
        hasNewIcon: true,
        hasSectionName: false,
        hasParagraph: false,
      },
      styles: VERTICAL_PACKAGE_CARD_STYLE_REST,
    },
    xl: {
      elements: {
        ...ARTICLE_CARD_DEFAULT_ELEMENTS,
        hasNewIcon: false,
        hasSectionName: true,
        hasParaWithTitle: true,
      },
      styles: VERTICAL_PACKAGE_CARD_STYLE_FIRST,
    },
    xxl: {
      elements: {
        ...ARTICLE_CARD_DEFAULT_ELEMENTS,
        hasNewIcon: true,
        hasSectionName: false,
        hasParaWithTitle: true,
      },
      styles: VERTICAL_PACKAGE_CARD_STYLE_FIRST,
    },
    featured: {
      elements: {
        ...ARTICLE_CARD_DEFAULT_ELEMENTS,
        hasNewIcon: true,
        hasSectionName: true,
        hasParaWithTitle: true,
      },
      styles: VERTICAL_PACKAGE_CARD_STYLE_FIRST,
    },
  },
  "horizontal-summary": {
    small: {
      elements: {},
      styles: {},
    },
    medium: {
      elements: {},
      styles: {},
    },
    "2md": {
      elements: {
        ...ARTICLE_CARD_DEFAULT_ELEMENTS,
        hasSectionName: true,
        hasParagraph: true,
        hasDateSideBySide: true,
      },
      styles: ARTICLE_CARD_HORIZONTAL_SUMMARY_DEFAULT_STYLE,
    },
    large: {
      elements: {
        ...ARTICLE_CARD_DEFAULT_ELEMENTS,
        hasSectionName: true,
        hasParagraph: true,
        hasDateSideBySide: true,
        hasIcon: true,
      },
      styles: {
        ...ARTICLE_CARD_HORIZONTAL_SUMMARY_DEFAULT_STYLE,
        sectionNameWrapper:
          "flex-col items-start md:flex-row md:items-center gap-1 md:gap-3",
      },
    },
    xl: {
      elements: {},
      styles: {},
    },
    xxl: {
      elements: {},
      styles: {},
    },
    featured: {
      elements: {},
      styles: {},
    },
  },
};
